import { lazy, Suspense } from "react";
import "./files.css";
import { useMediaSrc } from "./media.tsx";

export type DocumentViewProps = {
  /** A PDF. */
  src: string;
  /** Accessible name (the file name). */
  title: string;
  testID?: string;
};

type ViewerProps = { url: string; title: string; testID?: string };

/** The browser's own viewer in a frame. */
function FrameViewer({ url, title, testID }: ViewerProps) {
  return <iframe className="b-document-view" src={url} title={title} data-testid={testID} />;
}

// pdf.js loads on the first PDF opened; if its chunk can't load (offline, a new deploy), the frame still shows the file.
const PdfViewer = lazy(() => import("./PdfViewer.tsx").catch(() => ({ default: FrameViewer })));

/**
 * Whether the engine's built-in PDF viewer works in a frame and zooms: Chromium (Chrome, Edge, the Windows desktop
 * app's WebView2) and Firefox. Not WebKit (Safari, the macOS desktop app's WKWebView: no pinch or zoom controls in a
 * frame; Linux's WebKitGTK has no viewer at all) and not Android, which doesn't show PDFs inline.
 */
export function nativePdfViewerZooms(userAgent: string): boolean {
  return /\b(Chrome|Chromium|Edg|Firefox)\//.test(userAgent) && !/Android/.test(userAgent);
}

/**
 * An inline PDF viewer that fills its container, with zoom. Web: the browser's built-in viewer in a frame where it
 * zooms, else pdf.js (PdfViewer: toolbar, pinch, ctrl+wheel); native: PDFKit `PDFView` (iOS) / `PdfRenderer` pages in
 * a LazyColumn (Android).
 */
export function DocumentView({ src, title, testID }: DocumentViewProps) {
  const url = useMediaSrc(src);
  if (url === undefined) return <div className="b-document-view" data-testid={testID} />;
  if (nativePdfViewerZooms(navigator.userAgent)) return <FrameViewer url={url} title={title} {...(testID === undefined ? {} : { testID })} />;
  return (
    <Suspense fallback={<div className="b-document-view" data-testid={testID} />}>
      <PdfViewer url={url} title={title} {...(testID === undefined ? {} : { testID })} />
    </Suspense>
  );
}
